import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const CycleTime = () => {
  // State variables for Cycle Time calculation
  const [totalProductionTime, setTotalProductionTime] = useState(0); // Total production time
  const [unitsProduced, setUnitsProduced] = useState(0); // Units produced
  const [cycleTime, setCycleTime] = useState(0); // Calculated cycle time

  // Event handlers to update state values
  const handleProductionTimeChange = (e) => setTotalProductionTime(parseFloat(e.target.value) || 0);
  const handleUnitsProducedChange = (e) => setUnitsProduced(parseInt(e.target.value) || 0);

  // useEffect hook to calculate Cycle Time
  useEffect(() => {
    if (unitsProduced > 0) {
      // Cycle Time = Total Production Time / Units Produced
      const cycleTimeValue = totalProductionTime / unitsProduced;
      setCycleTime(cycleTimeValue);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [totalProductionTime, unitsProduced]);

  return (
    <Layout title="Cycle Time Calculator (Manufacturing)" path="cycle-time">
      <section className="section-padding" id="cycle-time">
        <div className="container mt-5">
          <div className="row">
            {/* Cycle Time Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div className="p-4 border rounded shadow" style={{ border: "1px solid black" }}>
                <h3 className="text-center mb-4">Cycle Time Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Total Production Time */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalProductionTime" className="form-label">Total Production Time (hours)
                      <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Production Time:</strong> The total amount of time it takes to complete production for all units in hours."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalProductionTime"
                        placeholder="Enter total production time in hours"
                        value={totalProductionTime || ""}
                        onChange={handleProductionTimeChange}
                      />
                    </div>

                    {/* Input for Units Produced */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="unitsProduced" className="form-label">Units Produced
                      <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Units Produced:</strong> The total number of units completed during the production process."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="unitsProduced"
                        placeholder="Enter number of units produced"
                        value={unitsProduced || ""}
                        onChange={handleUnitsProducedChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Cycle Time:{" "}
                        <span className="fw-bold ms-3">
                          {cycleTime === 0
                            ? "0"
                            : cycleTime.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{" "}
                          hours per unit
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">Introduction to Cycle Time</h3>

                {/* What is Cycle Time? */}
                <h4 className="mt-3 mb-3">What is Cycle Time?</h4>
                <p>
                  **Cycle Time** is the total time it takes to produce one unit from start to finish, including all processes such as setup, production, and quality checks. It is an essential metric for assessing production efficiency.
                </p>

                {/* Why Use Cycle Time? */}
                <h4 className="mt-4 mb-3">Why Use Cycle Time?</h4>
                <p>
                  Understanding cycle time helps manufacturers identify inefficiencies in the production process, optimize workflows, and improve overall production throughput. By tracking cycle time, companies can set realistic targets and improve their production processes.
                </p>

                {/* Benefits of Using Cycle Time */}
                <h4 className="mt-4 mb-3">Benefits of Using Cycle Time</h4>
                <ul>
                  <li><strong>Optimizes Production Efficiency:</strong> By monitoring cycle time, production processes can be streamlined for maximum efficiency.</li>
                  <li><strong>Helps Identify Bottlenecks in the Production Line:</strong> Monitoring cycle time helps pinpoint areas where production is slowed, allowing for corrective measures.</li>
                  <li><strong>Supports Lean Manufacturing Principles:</strong> A key element of lean manufacturing is reducing cycle time to eliminate waste and improve flow.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use Cycle Time?</h4>
                <p>
                  This calculator is suitable for:
                </p>
                <ul>
                  <li><strong>Manufacturing Teams:</strong> Helps production managers and floor supervisors track efficiency and set production goals.</li>
                  <li><strong>Process Engineers:</strong> Assists in identifying process improvements to optimize the production line.</li>
                  <li><strong>Quality Control:</strong> Helps identify and correct any delays in the production process that may affect product quality.</li>
                </ul>

                {/* Two Additional Questions */}
                <h4 className="mt-4 mb-3">How can cycle time be reduced in manufacturing?</h4>
                <p>
                  Cycle time can be reduced by streamlining production processes, improving equipment maintenance, reducing downtime, and enhancing worker training. Additionally, employing lean manufacturing techniques and automation can significantly cut cycle times.
                </p>

                <h4 className="mt-4 mb-3">Why is it important to monitor cycle time regularly?</h4>
                <p>
                  Regular monitoring of cycle time allows manufacturers to identify trends, address inefficiencies, and ensure production goals are met. It enables timely adjustments to optimize processes and maintain high levels of production output.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CycleTime;
